import React, { useRef } from "react";
import {
    Avatar,
    Button,
    Flex,
    Input,
    LinkedInIcon,
    Loader,
    TeamsIcon,
    Text,
} from "@fluentui/react-northstar";
import styled from "styled-components/macro";

import BadgeIcon from "../../BadgeIcon";
import CopyText from "../../CopyText";
import CustomBadge from "../../../../../components/Badge/Custom";
import { Link } from "react-router-dom";
import CallMobile from "../../CallMobile";
import { _get_str_status } from "../../../utils";
import { useMutation, useQueryClient } from "react-query";
import { _api_upload_avatar } from "../../../../../api/user/profile";
import { useAuthDetailStore } from "../../../../../stores/useAuthStore";
import { QUERY_KEY } from "../../../constants";
import { DEVICE } from "../../../../../constants/responsive";

const Header = ({
    full_name,
    status,
    avatar,
    poste,
    departement,
    headOffice,
    email,
    mobile,
    linkedin,
    matricule,
    isExternal,
    missingInformationsNumber,
    isMe = false,
    isHR = false,
}) => {
    const refInputFile = useRef(null);

    const clientQuery = useQueryClient();

    const { agency } = useAuthDetailStore((state) => state.detail);

    const { isLoading: isLoadingAvatar, mutate: mutateAvatar } = useMutation(
        (data) => _api_upload_avatar(data)
    );

    const _uploadAvatar = ({ file }) => {
        const id_user = useAuthDetailStore.getState().id_user;
        mutateAvatar(
            {
                uploaded_files: file,
                id_user,
            },
            {
                onSuccess: (data) => {
                    if (data?.success) {
                        clientQuery.refetchQueries(QUERY_KEY + "::header");
                    }
                    return;
                },
            }
        );
        return;
    };

    return (
        <HeaderWrapper fill column>
            {/* INPUT HIDDEN */}
            <Input
                type="file"
                hidden
                ref={refInputFile}
                onChange={(e) => {
                    const files = e?.target?.files;
                    if (!files?.length) {
                        return;
                    }
                    const file = files[0];
                    if (file?.size > 2_500_000) {
                        return console.log("max size exceeded");
                    }
                    _uploadAvatar({
                        file,
                    });
                    return;
                }}
            />
            <HeaderContent fill>
                {/* left section */}
                <HeaderLeft gap="gap.medium">
                    <Flex>
                        <AvatarStyle
                            image={avatar}
                            name={full_name}
                            size={"largest"}
                            className={isMe ? "editable" : ""}
                            onClick={
                                isMe
                                    ? () => refInputFile?.current?.click()
                                    : null
                            }
                        />
                        {isLoadingAvatar && (
                            <Loader
                                variables={{
                                    color: "white",
                                }}
                                styles={{
                                    position: "absolute",
                                    inset: 0,
                                }}
                            />
                        )}
                    </Flex>
                    <Flex column gap="gap.smaller" vAlign="center" fill>
                        <Flex gap="gap.smaller" vAlign="center">
                            <FullnameWrapper>
                                <FullName content={full_name} truncated />
                            </FullnameWrapper>
                            <Button
                                icon={<TeamsIcon size="large" />}
                                iconOnly
                                text
                                as="a"
                                href={`MSTeams:/l/chat/0/0?users=${email}`}
                            />
                        </Flex>
                        <Flex gap="gap.small">
                            <Poste content={poste} />
                            {isExternal && <External content={"(Externe)"} />}
                        </Flex>
                        <HeaderDepartmentAgency gap="gap.medium">
                            <BadgeIcon
                                icon={departement.icon}
                                content={departement.content}
                            />
                            <BadgeIcon
                                icon={headOffice.icon}
                                content={headOffice.content}
                            />
                        </HeaderDepartmentAgency>
                    </Flex>
                </HeaderLeft>
                {/* center section */}
                <HeaderCenter column gap="gap.small">
                    <Flex column gap="gap.small" hAlign="start">
                        <CopyText isEmail={true} content={email} />
                        <CallMobile content={mobile} />
                        {(isMe || isHR) &&
                            missingInformationsNumber !== 0 &&
                            missingInformationsNumber !== undefined && (
                                <MissingInformations
                                    content={
                                        missingInformationsNumber +
                                        " informations manquantes sur le profil"
                                    }
                                />
                            )}
                    </Flex>
                </HeaderCenter>
                {/* right section */}
                <HeaderMatriculation fill hAlign="end" gap="gap.smaller" column>
                    {isHR &&
                        agency?.country_code?.toLowerCase() !== "ch" &&
                        matricule?.content && (
                            <div>
                                <BadgeIcon
                                    icon={matricule.icon}
                                    content={matricule.content}
                                />
                            </div>
                        )}
                    <CustomBadge content={_get_str_status(status)} />
                </HeaderMatriculation>
            </HeaderContent>
            {linkedin && (
                <Flex hAlign="end">
                    <Flex styles={{ paddingRight: "15px" }}>
                        <Link
                            to={linkedin}
                            target="_blank"
                            style={{
                                textDecorationLine: "none",
                            }}
                        >
                            <Button
                                content={"Voir le profil"}
                                icon={<LinkedInIcon />}
                                iconPosition={"after"}
                                text
                            />
                        </Link>
                    </Flex>
                </Flex>
            )}
        </HeaderWrapper>
    );
};

export default Header;

const HeaderMatriculation = styled(Flex)`
    @media ${DEVICE.mobileS} {
        align-items: flex-start;
        flex: 0;
    }
    @media ${DEVICE.tablet} {
        padding: 32px 0px;
    }
    @media ${DEVICE.laptop} {
        padding: 32px 24px;
        align-items: flex-end;
    }
`;

const HeaderCenter = styled(Flex)`
    @media ${DEVICE.mobileS} {
        flex: 2;
        padding: 8px 0px;
        justify-content: center;
        align-items: center;
    }
    @media ${DEVICE.tablet} {
        padding: 32px 0px;
        align-items: center;
    }
    @media ${DEVICE.laptop} {
        padding: 32px 0px;
        align-items: flex-start;
    }
    @media ${DEVICE.desktop} {
        padding: 32px 0px;
        align-items: center;
    }
`;

const HeaderDepartmentAgency = styled(Flex)`
    width: fit-content;
    @media ${DEVICE.mobileS} {
        flex-direction: column;
    }
    @media ${DEVICE.mobileL} {
        flex-direction: row;
    }
`;

const HeaderLeft = styled(Flex)`
    align-items: center;
    @media ${DEVICE.mobileS} {
        flex: 2;
    }
`;

const HeaderContent = styled(Flex)`
    min-height: 160px;
    /* overflow: hidden; */
    @media ${DEVICE.mobileS} {
        flex-direction: column;
        padding: 8px;
    }
    @media ${DEVICE.tablet} {
        flex-direction: row;
        padding-inline: 8px;
        padding-block: 8px;
    }
    @media ${DEVICE.laptop} {
        padding-inline: 8px;
        padding-block: 0px;
    }
    @media ${DEVICE.desktop} {
        padding: 0px;
    }
`;

const HeaderWrapper = styled(Flex)``;

const AvatarStyle = styled(Avatar)`
    text-decoration: none;
    border-radius: 49%;
    overflow: hidden;
    text-align: center;
    color: white;
    :hover.editable {
        cursor: pointer;
        ::after {
            content: "";
            position: absolute;
            inset: -10px;
            border: 1px solid white;
            background-color: rgba(37, 36, 35, 0.65);
        }
        ::before {
            z-index: 2;
            content: "Modifier";
            position: absolute;
            top: 50%;
            right: 50%;
            transform: translate(50%, -50%);
            color: white;
        }
    }
`;

const MissingInformations = styled(Text)`
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #c4314b;
`;

const External = styled(Text)``;

const Poste = styled(Text)`
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #242424;
`;

const FullnameWrapper = styled(Flex)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @media ${DEVICE.mobileS} {
        max-width: 150px;
    }
    @media ${DEVICE.mobileM} {
        max-width: 200px;
    }
    @media ${DEVICE.mobileL} {
        max-width: 350px;
    }
    @media ${DEVICE.tablet} {
        max-width: 200px;
    }
`;

const FullName = styled(Text)`
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #242424;
`;
